import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { decodeToken } from 'react-jwt';
import Cookies from 'js-cookie';
import { getRegionsForEnv, getAccount } from '@/utils/api';
import { RegionAPI, DecodedToken, Account } from '@/utils/types';
import * as Sentry from '@sentry/react';

type AuthContext = {
  auth?: DecodedToken;
  loading?: boolean;
  region?: RegionAPI;
  account?: Account;
  setAuth?: (auth: DecodedToken) => void;
};

type AuthProviderProps = {
  children: ReactNode;
};

const AuthContext = createContext<AuthContext | undefined>(undefined);

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [auth, setAuth] = useState<DecodedToken | undefined>();
  const [region, setRegion] = useState<RegionAPI | undefined>();
  const [loading, setLoading] = useState<boolean>(true);
  const [account, setAccount] = useState<Account | undefined>();

  useEffect(() => {
    async function handleAuthAndRegion() {
      // If a token is provided as a URL GET parameter, set it as the auth cookie
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      if (urlToken) {
        Cookies.set('token', urlToken);
        Cookies.set('ArchireportOrigin', 'archireportelectron');
        // Clean the token from the URL to avoid leaking it
        urlParams.delete('token');
        const newSearch = urlParams.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      } else {
        Cookies.set('ArchireportOrigin', 'archireportweb');
      }

      const token = Cookies.get('token') as string;
      if (token) {
        const decodedToken = decodeToken<DecodedToken>(token);

        if (decodedToken && decodedToken.exp * 1000 > Date.now()) {
          // If token is not expired
          setAuth(decodedToken);

          const env = import.meta.env.VITE_REGION_ENV || 'prod';
          const regions = await getRegionsForEnv(env);
          const region = regions.find(region => region.short_region === decodedToken.region);

          if (region) {
            Cookies.set('region_user', JSON.stringify(region));
            setRegion(region);
          }

          if (decodedToken.account.id) {
            const loggedAccount = await getAccount();

            if (loggedAccount && loggedAccount._id) {
              setAccount(loggedAccount);
              Sentry.setUser({
                id: loggedAccount._id,
                email: loggedAccount?.properties?.email,
                username: `${loggedAccount?.properties?.firstname ?? ''} ${
                  loggedAccount?.properties?.lastname ?? ''
                }`.trim(),
              });
            }
          }
        }
      }

      setLoading(false);
    }

    handleAuthAndRegion();
  }, []);

  return <AuthContext.Provider value={{ auth, region, loading, account, setAuth }}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuthContext must be used within a AuthProvider');
  }

  return context;
};
